import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.sin
import kotlin.math.sqrt

/** Small, platform-independent geometry model. Gameplay remains the authority for the result. */
internal data class DiceVector(val x: Float, val y: Float, val z: Float) {
    operator fun plus(other: DiceVector) = DiceVector(x + other.x, y + other.y, z + other.z)
    operator fun minus(other: DiceVector) = DiceVector(x - other.x, y - other.y, z - other.z)
    operator fun times(scale: Float) = DiceVector(x * scale, y * scale, z * scale)
    fun dot(other: DiceVector) = x * other.x + y * other.y + z * other.z
    fun normalized(): DiceVector = this * (1f / sqrt(dot(this)))
}

internal data class DiceFace3D(
    val number: Int,
    val normal: DiceVector,
    val across: DiceVector,
    val down: DiceVector,
) {
    fun point(u: Float, v: Float) = normal + across * u + down * v
}

internal data class DiceSurface3D(
    val vertices: List<DiceVector>,
    val normal: DiceVector,
    val face: DiceFace3D? = null,
)

internal val diceFaces3D = listOf(
    DiceFace3D(1, DiceVector(0f, 0f, 1f), DiceVector(1f, 0f, 0f), DiceVector(0f, 1f, 0f)),
    DiceFace3D(6, DiceVector(0f, 0f, -1f), DiceVector(-1f, 0f, 0f), DiceVector(0f, 1f, 0f)),
    DiceFace3D(3, DiceVector(1f, 0f, 0f), DiceVector(0f, 0f, -1f), DiceVector(0f, 1f, 0f)),
    DiceFace3D(4, DiceVector(-1f, 0f, 0f), DiceVector(0f, 0f, 1f), DiceVector(0f, 1f, 0f)),
    DiceFace3D(2, DiceVector(0f, -1f, 0f), DiceVector(1f, 0f, 0f), DiceVector(0f, 0f, 1f)),
    DiceFace3D(5, DiceVector(0f, 1f, 0f), DiceVector(1f, 0f, 0f), DiceVector(0f, 0f, -1f)),
)

/** Fixed face markings: opposite sides sum to seven throughout the entire tumble. */
internal fun dicePips3D(number: Int): List<Pair<Float, Float>> {
    val a = 0.43f
    return when (number) {
        1 -> listOf(0f to 0f)
        2 -> listOf(-a to -a, a to a)
        3 -> listOf(-a to -a, 0f to 0f, a to a)
        4 -> listOf(-a to -a, a to -a, -a to a, a to a)
        5 -> listOf(-a to -a, a to -a, 0f to 0f, -a to a, a to a)
        6 -> listOf(-a to -a, a to -a, -a to 0f, a to 0f, -a to a, a to a)
        else -> emptyList()
    }
}

private fun DiceVector.rotateX(degrees: Float): DiceVector {
    val angle = degrees * (PI / 180).toFloat()
    val c = cos(angle)
    val s = sin(angle)
    return DiceVector(x, y * c - z * s, y * s + z * c)
}

private fun DiceVector.rotateY(degrees: Float): DiceVector {
    val angle = degrees * (PI / 180).toFloat()
    val c = cos(angle)
    val s = sin(angle)
    return DiceVector(x * c + z * s, y, -x * s + z * c)
}

private fun DiceVector.rotateZ(degrees: Float): DiceVector {
    val angle = degrees * (PI / 180).toFloat()
    val c = cos(angle)
    val s = sin(angle)
    return DiceVector(x * c - y * s, x * s + y * c, z)
}

internal fun orientDiceVector(
    vector: DiceVector,
    number: Int,
    progress: Float,
    variant: Int,
): DiceVector {
    val resultFacingCamera = when (number) {
        2 -> vector.rotateX(-90f)
        3 -> vector.rotateY(-90f)
        4 -> vector.rotateY(90f)
        5 -> vector.rotateX(90f)
        6 -> vector.rotateY(180f)
        else -> vector
    }
    val remaining = 1f - progress.coerceIn(0f, 1f)
    val direction = if (variant % 2 == 0) 1f else -1f
    // Put the result on the physical top (-Y), then view the table from above.
    // Yaw varies the two cubes without tilting their top faces away from the table.
    return resultFacingCamera
        .rotateX(90f)
        .rotateX(remaining * 540f * direction)
        .rotateY(remaining * 720f)
        .rotateZ(remaining * 90f * direction)
        .rotateY(32f * direction)
        .rotateX(-65f)
}

/** Perspective projection in die-local units; x/y are scaled by the Canvas afterwards. */
internal fun projectDiceVector(vector: DiceVector): Pair<Float, Float> {
    val perspective = DICE_CAMERA_DISTANCE / (DICE_CAMERA_DISTANCE - vector.z)
    return vector.x * perspective to vector.y * perspective
}

internal const val DICE_CAMERA_DISTANCE = 6f
internal const val DICE_PROJECTION_SCALE = 0.30f
internal const val DICE_3D_TUMBLE_DURATION_MS = 900

// Flat numbered faces, rounded edge strips, and spherical corners. Built once, not per frame.
internal val diceSurfaces3D: List<DiceSurface3D> = buildList {
    val halfFace = 0.78f
    val radius = 1f - halfFace
    val steps = 4
    val axes = listOf(DiceVector(1f, 0f, 0f), DiceVector(0f, 1f, 0f), DiceVector(0f, 0f, 1f))
    val signs = listOf(-1f, 1f)

    diceFaces3D.forEach { face ->
        add(DiceSurface3D(
            listOf(face.point(-halfFace, -halfFace), face.point(halfFace, -halfFace),
                face.point(halfFace, halfFace), face.point(-halfFace, halfFace)),
            face.normal, face,
        ))
    }

    for (a in 0..2) for (b in a + 1..2) {
        val lengthAxis = axes[3 - a - b]
        for (signA in signs) for (signB in signs) {
            val normalA = axes[a] * signA
            val normalB = axes[b] * signB
            val center = (normalA + normalB) * halfFace
            for (step in 0 until steps) {
                fun normalAt(i: Int): DiceVector {
                    val t = i.toFloat() / steps
                    return (normalA * (1f - t) + normalB * t).normalized()
                }
                val n0 = normalAt(step)
                val n1 = normalAt(step + 1)
                val start = center + n0 * radius
                val end = center + n1 * radius
                add(DiceSurface3D(
                    listOf(start - lengthAxis * halfFace, end - lengthAxis * halfFace,
                        end + lengthAxis * halfFace, start + lengthAxis * halfFace),
                    (n0 + n1).normalized(),
                ))
            }
        }
    }

    for (sx in signs) for (sy in signs) for (sz in signs) {
        val center = DiceVector(sx, sy, sz) * halfFace
        fun normalAt(i: Int, j: Int): DiceVector =
            DiceVector(sx * i, sy * j, sz * (steps - i - j)).normalized()
        fun triangle(a: DiceVector, b: DiceVector, c: DiceVector) {
            add(DiceSurface3D(listOf(center + a * radius, center + b * radius, center + c * radius),
                (a + b + c).normalized()))
        }
        for (i in 0 until steps) for (j in 0 until steps - i) {
            triangle(normalAt(i, j), normalAt(i + 1, j), normalAt(i, j + 1))
            if (i + j < steps - 1) {
                triangle(normalAt(i + 1, j), normalAt(i + 1, j + 1), normalAt(i, j + 1))
            }
        }
    }
}
